import { useMemo } from "react";

export const useCoworkingTotal = (cart) => {
  return useMemo(() => {
    return Object.values(cart?.coworking_extras_bolt_ons || {}).reduce(
      (sum, item) => {
        const price = parseFloat(item.price) || 0;
        const qty = parseInt(item.qty) || 0;
        return sum + price * qty;
      },
      0
    );
  }, [cart]);
};

export const getCoworkingExtrasTotalWithProration = (cart, startDate, endDate) => {
  const boltOns = Object.values(cart?.coworking_extras_bolt_ons || {});
  let fullPriceTotal = 0;
  let proratedTotal = 0;

  for (const item of boltOns) {
    const price = parseFloat(item.price) || 0;
    const qty = parseInt(item.qty) || 0;
    const itemTotal = price * qty;

    if (item.charge_full_price) {
      fullPriceTotal += itemTotal;
    } else {
      proratedTotal += itemTotal;
    }
  }

  const totalDaysInMonth = endDate;
  const remainingDays = endDate + 1 - startDate.getDate();
  const proratedAmount = (proratedTotal / totalDaysInMonth) * remainingDays;

  return fullPriceTotal + proratedAmount;
};


export const calculatePrice = (location, { howOftenDays, teamSize, howOften }) => {

  const unlimitedConfig = JSON.parse(location.unlimited_monthly_product_config);
  let totalDays = (howOften === 'week') ? (howOftenDays * 4.3) : howOftenDays;

  let unlimitedRollingPrice = (parseFloat(unlimitedConfig.monthly_rolling_price) || 0) * teamSize;
  let unlimitedSixMonthPrice = unlimitedRollingPrice - (unlimitedRollingPrice * (parseFloat(unlimitedConfig.six_month_discount_percentage) / 100));
  let unlimitedTwelveMonthPrice = unlimitedRollingPrice - (unlimitedRollingPrice * (parseFloat(unlimitedConfig.twelve_month_discount_percentage) / 100));
  let lowestUnlimitedPrice = Math.min(unlimitedRollingPrice, unlimitedSixMonthPrice, unlimitedTwelveMonthPrice);

  const flexConfig = JSON.parse(location.flex_day_pass_product_config)
  let flexRollingPrice = (parseFloat(flexConfig.monthly_rolling_price) || 0) * totalDays * teamSize;;
  let flexSixMonthPrice = flexRollingPrice - (flexRollingPrice * (parseFloat(flexConfig.six_month_discount_percentage) / 100));
  let flexTwelveMonthPrice = flexRollingPrice - (flexRollingPrice * (parseFloat(flexConfig.twelve_month_discount_percentage) / 100));
  let lowestFlexPrice = Math.min(flexRollingPrice, flexSixMonthPrice, flexTwelveMonthPrice);

  let recommended = 'unlimited';
  if (lowestUnlimitedPrice > lowestFlexPrice) {
    recommended = 'flex';
  }
  return {
    unlimited: {
      lowestPrice: lowestUnlimitedPrice.toFixed(2),
      rollingPrice: unlimitedRollingPrice.toFixed(2),
      sixMonthPrice: unlimitedSixMonthPrice.toFixed(2),
      twelveMonthPrice: unlimitedTwelveMonthPrice.toFixed(2),
      recommended: recommended,
      sixMonthPercent: unlimitedConfig.six_month_discount_percentage,
      twelveMonthPercent: unlimitedConfig.twelve_month_discount_percentage
    },
    flex: {
      lowestPrice: lowestFlexPrice.toFixed(2),
      rollingPrice: flexRollingPrice.toFixed(2),
      sixMonthPrice: flexSixMonthPrice.toFixed(2),
      twelveMonthPrice: flexTwelveMonthPrice.toFixed(2),
      recommended: recommended,
      sixMonthPercent: flexConfig.six_month_discount_percentage,
      twelveMonthPercent: flexConfig.twelve_month_discount_percentage
    },
  };

}


export const syncCartWithFilteredBoltOns = (filteredBoltOns, cart, setCart) => {
  const currentItems = { ...cart.coworking_extras_bolt_ons };
  const allowedSlugs = filteredBoltOns.map(item => item.slug);

  const boltOnPriceMap = {};
  filteredBoltOns.forEach(item => {
    boltOnPriceMap[item.slug] = item.chargeable_price_per_unit;
  });

  let hasChanged = false;

  const newItems = {};

  Object.keys(currentItems).forEach(slug => {
    if (!allowedSlugs.includes(slug)) {
      hasChanged = true;
    } else {
      const newPrice = boltOnPriceMap[slug];
      if (currentItems[slug].price !== newPrice) {
        hasChanged = true;
        newItems[slug] = {
          ...currentItems[slug],
          price: newPrice
        };
      } else {
        newItems[slug] = currentItems[slug];
      }
    }
  });
  if (hasChanged) {
    setCart(prev => ({
      ...prev,
      coworking_extras_bolt_ons: newItems
    }));
  }
};

export const customDatePrint = (date) => {
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear();
  return `${day}/${month}/${year}`;
};


export const customFormatDate = (date) => {
  const day = date.getDate();
  const month = date.toLocaleString("default", { month: "long" });
  const year = date.getFullYear();
  return `${day} ${month} ${year}`;
}

export const customFormatDateMY = (date) => {
  const day = date.getDate();
  const month = date.toLocaleString("default", { month: "long" });
  const year = date.getFullYear();
  return `${month} ${year}`;
}

export const addMonthsToDate = (date, months, startDate) => {
  let addMonths = months;
  if (months === 'no_mid_month') {
    addMonths = 0;
  }
  const newDate = new Date(date);
  newDate.setMonth(newDate.getMonth() + addMonths);
  newDate.setMonth(newDate.getMonth() + 1);
  if (months !== 'no_mid_month') {
    newDate.setDate(0);
  }
  else {
    if (startDate) {
      const daysPassed = new Date(startDate).getDate();
      newDate.setDate(newDate.getDate() - daysPassed);
    }
  }
  return newDate;
}


export const generatePaymentText = (startDate, paymentOption) => {
  const start = new Date(startDate);
  const day = start.getDate();
  const currentMonth = start.getMonth();
  const currentYear = start.getFullYear();

  const endOfThisMonth = new Date(currentYear, currentMonth + 1, 0);
  const nextMonthFirst = new Date(currentYear, currentMonth + 1, 1);

  let toPayNow = `${customFormatDate(start)} - ${customFormatDate(endOfThisMonth)}`;
  let toPayFrom = "";
  let var2EndDate;

  if (paymentOption === "twelve_month") {
    if (day === 1) {
      // currentMonth + 1
      var2EndDate = new Date(currentYear + 1, currentMonth, 0);
    } else {
      var2EndDate = new Date(currentYear, currentMonth + 12, day - 1);
    }
    toPayFrom = `${customFormatDateMY(nextMonthFirst)} - ${customFormatDateMY(var2EndDate)}`;
  } else if (paymentOption === "six_month") {
    if (day === 1) {
      var2EndDate = new Date(currentYear, currentMonth + 6, 0);
    } else {
      var2EndDate = new Date(currentYear, currentMonth + 6, day - 1);
    }
    toPayFrom = `${customFormatDateMY(nextMonthFirst)} - ${customFormatDateMY(var2EndDate)}`;
  } else if (paymentOption === "rolling_monthly") {
    toPayNow = `${customFormatDate(start)}`;
    toPayFrom = `${customFormatDate(nextMonthFirst)}`;
  }

  return { toPayNow, toPayFrom };
};


export const calculateTotalDays = (howOften, howOftenDays, teamSize) => {
  let totalDays = (howOften == "week") ? Math.ceil(Number(howOftenDays * 4.3)) : howOftenDays;
  return totalDays * Number(teamSize)
}

export const calculateVAT = (price) => {
  return (Number(price) * 0.2).toFixed(2);
}

export const getNextValidDate = (startFrom = new Date()) => {
  let date = new Date(startFrom);
  while (!isValidDate(date)) {
    date.setDate(date.getDate() + 1);
  }
  return date;
};

export const isWeekend = (date) => {
  const day = date.getDay();
  return day === 0 || day === 6;
};

const restrictedDatas = Array.isArray(CS_WIZ_START_DATE_RESTRICTION)
  ? CS_WIZ_START_DATE_RESTRICTION
  : [];

const restrictedDates = restrictedDatas.map((item) => {
  const [day, month, year] = item.date.split("/").map(Number);
  return new Date(year, month - 1, day);
});

export const isRestrictedDate = (date) => {
  return restrictedDates.some(
    (d) => d.toDateString() === date.toDateString()
  );
};

export const isValidDate = (date) => {
  return !isWeekend(date) && !isRestrictedDate(date);
};